import React from 'react';
import { useDispatch, useSelector, useState } from 'react-redux';
import { isEmpty, slice } from 'lodash';
import TextField from '@mui/material/TextField';  
import Autocomplete from '@mui/material/Autocomplete';   

import { setFacetOptions, resetFacets, facetsListSelector } from '../../../reducers/facetsReducer';
import { fetchLocations } from '../../../actions/locationActions';
import NoResults from './NoResults';

const getfacetByName = (facets, searchName) => {
  return facets.filter((facet) => facet.name === searchName)[0];
};

const SelectFacetName = ({ facets, onFacetChange }) => {
  const dispatch = useDispatch();

  const handleFacetNameChange = (ev) => {
    const { value } = ev.target;
    const selectedFacetData = getfacetByName(facets, value);
    const selectedFacet = {
      category: value,
      label: selectedFacetData.label,
      options: [],
    };
    dispatch(setFacetOptions(selectedFacet));
  };

  return (
    <div className="facets-selector__name facet-name">
      <select
        id="facet-name__input"
        onChange={handleFacetNameChange}
        defaultValue="default"
      >
        <option value="default" disabled>Select facet name</option>
        { facets.map((facet) =>
          (<option value={facet.name} key={facet.name}>{facet.label}</option>))
        }
      </select>
    </div>
  );
};

const SelectedFacetNameDisplay = ({ selectedFacetData, onResetFacets, onFacetChange }) => {
  const handleResetFacets = () => {
    onResetFacets();
    onFacetChange({}, true); // force fetch locations with not facet filter if the facet is not complete.
  };
  return (<>
    <label value="default" htmlFor="facet-categories__input">Selected Facet:</label>
    <span className='facets-selector__name-display'>
      {selectedFacetData.label}
      <a className='facets-selector__reset-action' onClick={handleResetFacets}>
        <i className="bi bi-x-lg"></i>
      </a>
    </span>
  </>);
};

const CategoryOptions = ({ facets, activeFacetName, activeFacetOptions, activeFacetLabel, onFacetChange }) => {
  const activeFacetData = facets.filter((facet) => facet.name === activeFacetName)[0];

  if(isEmpty(activeFacetName) || isEmpty(activeFacetData)) {
    return (<></>);
  }

  const { values: options } = activeFacetData;
  const dispatch = useDispatch();

  const isFacetReadyToFilter = ({ category, options }) => {
    if (!category || category === '') {
      return false;
    }

    if (!options || options.length < 1) {
      return false;
    }

    return true;
  };

  const handleCategoryOptionChange = (ev, values) => {
    const selectedFacet = {
      category: activeFacetName,
      label: activeFacetLabel,
      options: values,
    };

    const facetIsReady = isFacetReadyToFilter(selectedFacet);

    if(facetIsReady) {
      onFacetChange(selectedFacet);
    } else {
      onFacetChange(selectedFacet, true); // force fetch locations with not facet filter if the facet is not complete.
    }

    dispatch(setFacetOptions(selectedFacet));
  };

  return (
    <div className='facets-selector__options facet-options'>
      <label value="default" htmlFor="facet-categories__input">Select one or more options</label>
      <Autocomplete
        id="facet-options__input"
        multiple
        size="small"
        options={options}
        getOptionLabel={(option) => option}
        defaultValue={activeFacetOptions || []}
        filterSelectedOptions
        onChange={handleCategoryOptionChange}
        renderInput={(params) => (
          <TextField {...params} hiddenLabel placeholder="Select one or more values" />)
        }
      />
    </div>
  );
};

function FacetsSelector() {
  const { facetsData, selectedFacet, mapConfig, pageData } = useSelector(facetsListSelector);
  const { category: activeFacetName, options: activeFacetOptions, label: activeFacetLabel } = selectedFacet;
  const selectedFacetData = getfacetByName(facetsData, activeFacetName);
  const mainClass = 'facets-selector';

  const dispatch = useDispatch();
  const handleResetFacets = () => {
    dispatch(resetFacets());
  };

  const handleFacetChange = (selectedFacet, forceEmptyFacetFetch) => {
    const { zoom, mapBounds } = mapConfig;
    const { options } = selectedFacet;
    if (forceEmptyFacetFetch) {
      dispatch(fetchLocations({ pageData, zoom, mapBounds, selectedFacet: {} })); // force fetch locations with not facet filter if the facet is not complete.
      return;
    }
    dispatch(fetchLocations({ pageData, zoom, mapBounds, selectedFacet }));
  };

  return isEmpty(facetsData) ? 
    (
      <div className={mainClass}>
        <NoResults onResetFacets={handleResetFacets}/>
      </div>
    ) : (
    <div className={mainClass}>
      { isEmpty(selectedFacetData) ?
        (<SelectFacetName
          facets={facetsData || []}
          activeFacetName={activeFacetName}
          selectedFacet={selectedFacet}
          onFacetChange={handleFacetChange}
        />) :
        (<SelectedFacetNameDisplay 
          selectedFacetData={selectedFacetData}
          onResetFacets={handleResetFacets}
          onFacetChange={handleFacetChange}
          />)
      }
      {
        isEmpty(activeFacetName) ? (<></>) :
          <CategoryOptions
            facets={facetsData}
            activeFacetName={activeFacetName}
            activeFacetOptions={activeFacetOptions}
            activeFacetLabel={activeFacetLabel}
            onFacetChange={handleFacetChange}
          />
      }
    </div>
  );
}

export default FacetsSelector;